import React, { useState } from 'react';
import HeaderLogo from 'components/common/headerLogo';
import { Dayjs } from 'dayjs';
import Button from 'components/common/button';
import { PatientDOBDTO } from 'types/patients';
import {
  formatDateOnly,
  getLinkQueryParams,
  isEmptyString,
  parseErrorMessage,
} from 'utils/helper';
import dynamic from 'next/dynamic';
import Spinner from 'components/spinner/Spinner';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import { useDispatch, useSelector } from 'react-redux';
import useDisclaimerCommunique from 'hooks/useDisclaimerCommunique';
import { RootState } from 'store/store';
import { NullableCommunique } from 'types/types';
import { UseGetOrganizationByPatientId, useGetTokenByLink, UseVerifyPatientDOBById } from 'hooks/useApi';
import { organizationAction } from 'reduxStateSlice/organization';
import { useRouter } from 'next/router';

const MuiDatePicker = dynamic(() => import('components/common/datePicker'), { ssr: false });

function DisclaimerMessage() {
  const router = useRouter();
  const { token, emrType } = getLinkQueryParams(router.query);
  const dispatch = useDispatch();
  const [date, setDate] = useState<Dayjs | null>(null);
  const [dobErrorMessage, setDobErrorMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const { errorMessage, isLoading } = useDisclaimerCommunique();
  const { mutateAsync: getTokenByLink } = useGetTokenByLink();
  const { mutateAsync: getOrganizationByPatientId } = UseGetOrganizationByPatientId();
  const { mutateAsync: verifyPatientDOBById } = UseVerifyPatientDOBById();

  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;

  const onChangeDOB = (dob: Dayjs | null) => {
    setDate(dob);
    setDobErrorMessage('');
  };

  const onSubmit = async () => {
    if (!date?.isValid()) {
      setDobErrorMessage('Date Of Birth is required.');
    } else {
      const payload: PatientDOBDTO = {
        patientId: communiqueData?.patientId ?? 0,
        dateOfBirth: formatDateOnly(date ?? ''),
      };
      try {
        setIsVerifying(true);
        await getTokenByLink({
          hash: token ?? '',
          dateOfBirth: formatDateOnly(date ?? ''),
          emrType,
        });
        await verifyPatientDOBById(payload);
        const orgInfo = await getOrganizationByPatientId(
          { patientId: communiqueData?.patientId ?? 0 },
        );
        dispatch(organizationAction(orgInfo));
        dispatch(
          appStateSettingsAction({ name: ModuleTypes.patientList, page: null }),
        );
      } catch (error) {
        setDobErrorMessage(parseErrorMessage(error, 'Date of birth does not match our records. Contact the doctor’s office to verify.'));
      } finally {
        setIsVerifying(false);
      }
    }
  };

  return (
    <div className="app-page-container">
      {isLoading && (
        <div className="flex min-h-screen items-center justify-center bg-white">
          <Spinner />
        </div>
      )}
      {!isLoading && errorMessage && (
        <div className="flex min-h-screen items-center justify-center bg-white px-3">
          <p className="error-message">{errorMessage}</p>
        </div>
      )}
      {!isLoading && !errorMessage && (
        <div className="px-3">
          <HeaderLogo />
          <div className="mt-8">
            <p className="my-3">The contents of this form has been secured to protect the patient&apos;s information. Please enter the patient&apos;s Date Of Birth to continue.</p>
            <div>
              {dobErrorMessage && <p className="error-message">{dobErrorMessage}</p>}
              <MuiDatePicker
                onChange={onChangeDOB}
                disableFuture
                value={date}
                className="!w-full"
                error={!isEmptyString(dobErrorMessage)}
              />
            </div>
            {!isVerifying && (
              <div className="my-4">
                <Button onClick={onSubmit}>
                  Submit
                </Button>
              </div>
            )}
          </div>

          <div className="my-6">
            {isVerifying && (
              <div className="flex flex-auto justify-center items-center text-xs">
                <Spinner />
                Loading...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DisclaimerMessage;
